import { throttle, each } from "underscore";
import { defaultUrls } from "./defaults";

import {
  setTransactionPending,
  startTransaction,
  setTransactionSuccessful,
  failRequest,
  failTransaction
} from "../actions/network";

export const NETWORK_STATES = {
  PENDING: "PENDING",
  FETCHING: "FETCHING",
  ERROR: "ERROR",
  FETCHED: "FETCHED"
};

export const REPLACED = "REPLACED";

const $ = window.$;
const urls = window.urls || defaultUrls;
const maxRetries = 5;
const delay = 1000; // Max of one request per second

export default class NetworkManager {
  constructor() {
    this.requests = {};
    this.requestTypes = {
      postState: {
        defaultArgs: {
          type: "POST",
          url: urls.state,
          contentType: "application/json; charset=utf-8"
        },
        throttle: true,
        retryOnError: true
      },
      getStatus: {
        defaultArgs: {
          url: urls.status,
          cache: false,
          dataType: "json"
        },
        throttle: false
      },
      getFrameLists: {
        defaultArgs: {
          contentType: "application/json; charset=utf-8",
          cache: false,
          dataType: "json"
        },
        throttle: false
      }
    };

    each(
      this.requestTypes,
      (requestData, requestType) => {
        if (requestData.throttle) {
          this.requests[requestType] = throttle(
            this.sendRequest.bind(this, requestType, requestData.defaultArgs),
            delay,
            { leading: false }
          );
        } else {
          this.requests[requestType] = this.sendRequest.bind(
            this,
            requestType,
            requestData.defaultArgs
          );
        }
      },
      this
    );

    const getImageDefaultArgs = {
      contentType: false,
      cache: true
    };

    // This is a special case for images, since image requests are per-panel. In order
    // to allow multiple image requests across the entire workspace, each request is
    // given a unique ID based on the panel ID. Subsequent image requests with the same
    // panel ID will cancel the previous only for that panel.
    this.requests.getImage = (panelId, callerSuccessCallback, overrideArgs) => {
      this.sendRequest(
        "getImage" + panelId,
        getImageDefaultArgs,
        callerSuccessCallback,
        overrideArgs
      );
    };
  }

  seedStore(store) {
    this.store = store;
  }

  sendRequest(requestType, defaultArgs, callerSuccessCallback, overrideArgs) {
    const args = {
      ...defaultArgs,
      ...overrideArgs
    };

    // Clear out any old requests for this type, update args, reset error count
    this.store.dispatch(setTransactionPending(requestType, args));

    this._ajax(requestType, args, callerSuccessCallback);
  }

  _ajax(requestType, args, callerSuccessCallback) {
    // Send the actual ajax request
    const jqXHR = $.ajax(args)
      .done(this.onRequestSuccess.bind(this, requestType))
      .done(callerSuccessCallback)
      .fail(this.onRequestError.bind(this, requestType, callerSuccessCallback));

    // Set the jqXHR object for this transaction so we can cancel it later, if need be.
    // Also updates the status of this network request
    this.store.dispatch(startTransaction(requestType, jqXHR));
  }

  onRequestSuccess(requestType) {
    // Tell the store that we've succeeded
    this.store.dispatch(setTransactionSuccessful(requestType));
  }

  onRequestError(
    requestType,
    callerSuccessCallback,
    jqXHR,
    textStatus,
    errorThrown
  ) {
    // If we aborted this request because a new request replaced it, we don't need to do any
    // more handling
    if (textStatus === REPLACED) {
      return;
    }

    const transaction = this.getTransaction(requestType);
    // If we haven't reached the threshold of request
    if (transaction.requestErrorCount < maxRetries) {
      // Increment error count
      this.store.dispatch(failRequest(requestType));

      // Start a new request with a delay
      setTimeout(
        function () {
          this._ajax(requestType, transaction.args, callerSuccessCallback);
        }.bind(this),
        this.calculateTimeout(
          this.getTransaction(requestType).requestErrorCount
        )
      );
    } else {
      this.store.dispatch(failTransaction(requestType));

      // Some requests we want to retry, from the start, in 30 seconds if we get into an error state
      if (this.requestTypes[requestType].retryOnError) {
        setTimeout(() => {
          this.requests[requestType](callerSuccessCallback, transaction.args);
        }, 30 * 1000);
      }
    }
  }

  getTransaction(requestType) {
    return this.store.getState().networkTransactions[requestType];
  }

  calculateTimeout(requestErrorCount) {
    return requestErrorCount * 1000;
  }
}
